import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

// API service for regulation detail tabs content - Based on actual API response structure

export interface PenaltyTier {
  tier: number;
  culpability: "Civil" | "Criminal";
  minPenalty: number | null;
  maxPenalty: number;
  annualCap: number | null;
  text: string;
}

export interface EnforcementRule {
  title: string;
  titleNumber?: string;
  part?: string;
  section?: string;
  text: Record<string, string>;
  penaltyTiers: PenaltyTier[];
  fines: {
    effectiveDate: string;
    adjustmentBasis: string;
  };
  mitigations: {
    factorsConsidered: string[];
    abatementImpact: string;
  };
  proceduralRights: {
    notice: string;
    contest: string;
    appeal: string;
  };
  agencySpecificNotes: string;
  officialSources: string[];
}

export interface PenaltiesData {
  regulation: {
    name: string;
    enforcementRule: EnforcementRule[];
  };
}

export interface Training {
  title: string;
  provider: string;
  url?: string;
  format: "online" | "hybrid" | "in-person";
  targetRoles: string[];
  duration: string;
  cost: string;
  certification: boolean;
  prerequisites: string | null;
  description: string;
  level: "basic" | "intermediate" | "advanced";
}

export interface TrainingsData {
  regulation: {
    group: string;
    subject: string;
    roles: string[];
  };
  trainings: Training[];
  compliance: {
    mandatoryTraining: {
      role: string;
      requiredTraining: string[];
      frequency: string;
    }[];
    recommendedTraining: string[];
    refresherRequirements: string;
  };
  certifications: {
    name: string;
    issuer: string;
    validityPeriod: string;
    renewalRequirements: string;
    url?: string;
  }[];
  resources: {
    title: string;
    type: "guide" | "manual" | "publication" | "video" | "toolkit";
    url: string;
    description: string;
  }[];
}

export interface EnforcementAction {
  caseDetails: {
    caseName: string;
    docketNumber: string | null;
    year: number;
  };
  company: string;
  violation: {
    type: string;
    legalBasis: string;
  };
  penalty: {
    amount: number;
    currency: string;
    basis: string;
    statutoryReference: string;
  };
  complianceObligations: string[];
  repeatOffender: string | null;
  officialSources: string[];
}

export interface FineAnalysisData {
  enforcement: {
    authority: string;
    enforcementAction: EnforcementAction[];
    trendAnalysis: {
      [year: string]: {
        totalFines: number;
        largestFine: {
          company: string;
          amount: number;
        };
        industryPatterns: string;
      };
    };
  };
}

export interface FineAnalysisRequest {
  regulation_id: string;
  entity_type: string;
  state: string;
  county: string;
  city: string;
  additional_parameters?: Record<string, any>;
}

export interface RegulationSourceDocumentSection {
  id?: number | string;
  anchor_id?: string | null;
  heading?: string | null;
  heading_path?: string[] | string | null;
  text?: string | null;
  excerpt?: string | null;
  source_url?: string | null;
  cfr_references?: string[];
}

export interface RegulationSourceDocument {
  id?: number | string;
  source_system: string;
  document_number: string;
  citation?: string | null;
  title: string;
  publication_date?: string | null;
  effective_date?: string | null;
  source_url?: string | null;
  html_url?: string | null;
  pdf_url?: string | null;
  cfr_references?: string[];
  sections?: RegulationSourceDocumentSection[];
}

export interface RegulationSourceDocumentsResponse {
  regulation?: string;
  documents: RegulationSourceDocument[];
  count?: number;
}

export interface RegulationSourceDocumentsParams {
  regulation: string;
  document_number?: string;
  anchor_id?: string;
  cfr_part?: string;
}

// API endpoints
const BASE_URL = getApiOrigin();

export async function getRegulationSourceDocuments(
  params: RegulationSourceDocumentsParams,
): Promise<RegulationSourceDocumentsResponse> {
  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const query = new URLSearchParams({
      regulation: params.regulation,
    });
    if (params.document_number?.trim()) {
      query.set("document_number", params.document_number.trim());
    }
    if (params.anchor_id?.trim()) {
      query.set("anchor_id", params.anchor_id.trim());
    }
    if (params.cfr_part?.trim()) {
      query.set("cfr_part", params.cfr_part.trim());
    }

    const response = await fetch(
      `${BASE_URL}/api/v1/regulations/source-documents?${query.toString()}`,
      {
        method: "GET",
        headers,
      },
    );

    check401(response);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch regulation source documents: ${response.statusText}`,
      );
    }

    return (await response.json()) as RegulationSourceDocumentsResponse;
  } catch (error) {
    console.error("Error fetching regulation source documents:", error);
    throw new Error("Failed to load regulation source documents");
  }
}

export async function getPenaltiesData(payload: any): Promise<PenaltiesData> {
  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/api/v1/penalties/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      throw new Error(`Failed to fetch penalties data: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching penalties data:', error);
    throw new Error('Failed to load penalties data');
  }
}

export async function getTrainingsData(payload: any): Promise<TrainingsData> {
  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/api/v1/trainings/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      throw new Error(`Failed to fetch trainings data: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching trainings data:', error);
    throw new Error('Failed to load trainings data');
  }
}

// Generic payload variant to support alternate fine analysis request shapes
export async function getFineAnalysisData(payload: any): Promise<FineAnalysisData> {
  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/api/v1/fines/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      throw new Error(`Failed to submit fine analysis request: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting fine analysis request:', error);
    throw new Error('Failed to process fine analysis request');
  }
}



